import React, { useContext, useEffect } from 'react';
import s from './DeviceAuthorize.scss';
import { AppContextProvider } from '../AppLoader';
import { Box, Loader } from '@wix/design-system';
import QRCode from 'react-qr-code';
import { dataHooks } from '../../dataHooks';
import { DialogFormContainer } from '../DialogContainer';
import { BodyContainer } from '../AuthPage/AuthPage';
import { keys } from '../../translationsKeys';
import { EXPERIMENTS } from '../../utils/constants';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  ThemedHeading,
  ThemedText,
  ThemedButton,
  ThemedTextButton,
} from '../ThemedComponents';

interface DeviceAuthorizeProps {}

export const DeviceAuthorize: React.FC<DeviceAuthorizeProps> = observer(() => {
  const {
    rootStore: {
      deviceAuthorizeStore,
      navigationStore,
      experiments,
      displayStore: { isMobile },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  // Manual user_code entry isn't supported yet — gate the code display behind a FT.
  const showUserCode = experiments.enabled(
    EXPERIMENTS.DEVICE_AUTH_SHOW_USER_CODE,
  );

  useEffect(() => {
    deviceAuthorizeStore.start();
    return () => deviceAuthorizeStore.dispose();
  }, [deviceAuthorizeStore]);

  const { status } = deviceAuthorizeStore;
  const isError = status === 'error';
  const qrReady = status === 'pending' && !!deviceAuthorizeStore.qrValue;

  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.deviceAuthorize.container}
        formClasses={[s.deviceAuthorize]}
        mainHeaderProps={{ showBackButton: false }}
      >
        <BodyContainer>
          <Box marginBottom={3} verticalAlign="middle" textAlign="center">
            <ThemedHeading
              appearance={isMobile ? 'H2' : 'H1'}
              dataHook={dataHooks.deviceAuthorize.title}
            >
              {t(keys['device.authorize.title'])}
            </ThemedHeading>
          </Box>

          {isError ? (
            <Box
              marginBottom={3}
              direction="vertical"
              align="center"
              textAlign="center"
              gap="SP4"
              dataHook={dataHooks.deviceAuthorize.error}
            >
              <ThemedText size="medium">
                {deviceAuthorizeStore.errorMessage}
              </ThemedText>
              <ThemedButton
                size="large"
                skin="standard"
                onClick={() => deviceAuthorizeStore.start()}
                dataHook={dataHooks.deviceAuthorize.retry}
              >
                {t(keys['device.authorize.retry'])}
              </ThemedButton>
            </Box>
          ) : (
            // Loading and pending share the exact same layout — only the QR slot
            // swaps (spinner ↔ QR), so the card height never jumps on transition.
            <>
              <Box marginBottom={3} verticalAlign="middle" textAlign="center">
                <ThemedText size="small">
                  {t(keys['device.authorize.subtitle'])}
                </ThemedText>
              </Box>
              <Box marginBottom={3} className={s.qrSlot}>
                {qrReady ? (
                  <Box
                    align="center"
                    className={s.qrWrapper}
                    dataHook={dataHooks.deviceAuthorize.qrCode}
                  >
                    <QRCode value={deviceAuthorizeStore.qrValue} size={180} />
                  </Box>
                ) : (
                  <Loader size="medium" />
                )}
              </Box>
              {showUserCode && qrReady && (
                <Box
                  marginBottom={3}
                  direction="vertical"
                  align="center"
                  textAlign="center"
                >
                  <ThemedText size="small">
                    {t(keys['device.authorize.user.code.label'])}
                  </ThemedText>
                  <ThemedHeading
                    appearance="H3"
                    dataHook={dataHooks.deviceAuthorize.userCode}
                  >
                    {deviceAuthorizeStore.userCode}
                  </ThemedHeading>
                </Box>
              )}
              <Box
                marginBottom={3}
                align="center"
                verticalAlign="middle"
                gap="SP2"
                dataHook={dataHooks.deviceAuthorize.waiting}
              >
                <Loader size="tiny" />
                <ThemedText size="small">
                  {t(keys['device.authorize.waiting'])}
                </ThemedText>
              </Box>
            </>
          )}

          {/* Low-prominence escape hatch to the regular sign-in flow. */}
          <Box marginTop={3} verticalAlign="middle">
            <ThemedTextButton
              size="small"
              skin="dark"
              underline="always"
              onClick={() => navigationStore.navigateToLogin()}
              dataHook={dataHooks.deviceAuthorize.regularSignin}
            >
              {t(keys['device.authorize.regular.signin.cta'])}
            </ThemedTextButton>
          </Box>
        </BodyContainer>
      </DialogFormContainer>
    </Box>
  );
});
